import { MdOutlineArrowForwardIos } from "react-icons/md";
import Link from "next/link";
import { gsap } from "gsap";
import { useRef } from "react";
import { useGSAP } from "@gsap/react";
import { Button } from "@nextui-org/react";

interface LeftProps {
  currentIndex: number;
}

const Left: React.FC<LeftProps> = (props) => {
  const { currentIndex } = props;
  const containerRef = useRef(null);

  const slides = [
    {
      small1: "Quick metro delivery,",
      small2: "for free",
      title: "Get used to better living space",
      description1: "Shop quality, stylish and smart furniture here.",
      description2: "Check out fresh design ideas.",
      link: "/category/living-and-dining-room",
    },
    {
      small1: "Furnish Your Living Room,",
      small2: "with Ease",
      title: "Relax and Entertain in Comfort",
      description1: "Transform your living room into a stylish haven.",
      description2: "Shop our latest designs and elevate your space.",
      link: "/category/living-and-dining-room",
    },
    {
      small1: "Top-Rated Products,",
      small2: "Just for You!",
      title: "Create Your Perfect Sleep Sanctuary",
      description1:
        "Create the ultimate sleep sanctuary with our curated bedroom collections.",
      description2: "Rest well, live well.",
      link: "/category/bedroom-and-mattress",
    },
    {
      small1: "Discover Dining Furniture That",
      small2: "Fits Your Style!",
      title: "Dine Together in Style and Comfort",
      description1:
        "Create a warm, inviting atmosphere with our stylish dining sets,",
      description2: "perfect for every occasion.",
      link: "/category/dining-and-kitchen",
    },
    {
      small1: "Easy Returns for Wardrobe Purchases.",
      small2: "Shop Confidently!",
      title: "Maximize Space with Stylish Wardrobes",
      description1: "Maximize your space with sleek, functional wardrobes.",
      description2: "Designed for both style and convenience.",
      link: "/category/wardrobes/product",
    },
    {
      small1: "Upgrade Your Home Today:",
      small2: " Flexible Payment Plans Available",
      title: "Efficient Appliances for Everyday Life",
      description1:
        "Discover smart, efficient appliances that make life easier,",
      description2: "so you can focus on what matters most.",
      link: "/category/appliance",
    },
  ];

  const studentSlides = [
    {
      small1: "Start the Semester Right,",
      small2: "Furnish Your Space with Ease!",
      title: "Student Dorm & Apartment Essentials",
      description1: "Find affordable, stylish furniture for student living.",
      description2: "Shop now and settle in comfortably!",
      link: "/category/student-package/product",
    },
    {
      small1: "Move-in Ready,",
      small2: "Hassle-Free Delivery!",
      title: "Furniture Designed for Student Life",
      description1:
        "From study desks to cozy beds, we’ve got everything you need.",
      description2: "Fast delivery across Australia – no stress, just comfort!",
      link: "/category/bed-frames/product",
    },
    {
      small1: "Maximize Space,",
      small2: "Live Smart!",
      title: "Compact & Functional Furniture for Students",
      description1:
        "Smart storage solutions and space-saving furniture for small rooms.",
      description2:
        "Perfect for dorms, shared housing, and student apartments!",
      link: "/category/study-chair/product",
    },
    {
      small1: "Study Better,",
      small2: "Stay Focused!",
      title: "The Perfect Study Setup",
      description1:
        "Ergonomic desks and chairs for a comfortable learning environment.",
      description2: "Stay productive with a setup built for success!",
      link: "/category/study-desk/product",
    },
    {
      small1: "Good Sleep,",
      small2: "Better Student Life!",
      title: "Comfortable Beds & Mattresses",
      description1:
        "Find the perfect bed to recharge after long study sessions.",
      description2: "Affordable, durable, and ready for your new home!",
      link: "/category/bed-frames/product",
    },
    {
      small1: "Essential Appliances,",
      small2: "For Every Student Home!",
      title: "Make Life Easier with Smart Appliances",
      description1:
        "Microwaves, mini-fridges, and kettles – everything you need for daily convenience.",
      description2: "Affordable prices, student-friendly solutions!",
      link: "/category/appliance",
    },
  ];

  useGSAP(
    () => {
      const items = gsap.utils.toArray(".item-mama");
      gsap.fromTo(
        items,
        { y: 50, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          stagger: 0.5,
          ease: "power1.out",
          duration: 1,
          delay: 0.2,
        },
      );
    },
    { dependencies: [currentIndex], scope: containerRef },
  );

  return (
    <>
      <div className="bg-white bg-opacity-60 py-4 px-2 lg:py-16 lg:px-8 transition-all duration-500 block lg:hidden">
        <p className="text-sm md:text-base lg:text-lg transition-all duration-500">
          {slides[0].small1}{" "}
          <span className="text-icon">{slides[0].small2}</span>
        </p>
        <h2 className="text-base sm:text-2xl md:text-4xl mt-2 lg:text-6xl transition-all duration-500">
          {slides[0].title.split(" ").slice(0, 3).join(" ")} <br />{" "}
          {slides[0].title.split(" ").slice(3).join(" ")}
        </h2>
        <p className="mt-3 sm:mt-6 text-sm lg:text-base transition-all duration-500">
          {slides[0].description1}
        </p>
        <p className="text-sm lg:text-base transition-all duration-500">
          {slides[0].description2}
        </p>
        <Link href={slides[currentIndex].link}>
          <Button className="bg-icon text-white text-sm lg:text-base rounded-md px-2 sm:px-4 pt-6 pb-7 flex items-center gap-4 mt-4 sm:mt-8 transition-all duration-500">
            Start Shopping <MdOutlineArrowForwardIos />
          </Button>
        </Link>
      </div>
      <div
        ref={containerRef}
        className="bg-white bg-opacity-60 py-4 px-2 lg:py-16 lg:px-8 transition-all duration-500 hidden lg:block"
      >
        <p className="item-mama opacity-0 text-sm md:text-base lg:text-lg transition-all duration-500">
          {slides[currentIndex].small1}{" "}
          <span className="text-icon">{slides[currentIndex].small2}</span>
        </p>
        <h2 className="item-mama opacity-0 text-base sm:text-2xl md:text-4xl mt-2 lg:text-6xl transition-all duration-500">
          {slides[currentIndex].title.split(" ").slice(0, 3).join(" ")} <br />{" "}
          {slides[currentIndex].title.split(" ").slice(3).join(" ")}
        </h2>
        <p className="item-mama opacity-0 mt-3 sm:mt-6 text-sm lg:text-base transition-all duration-500">
          {slides[currentIndex].description1}
        </p>
        <p className="item-mama opacity-0 text-sm lg:text-base transition-all duration-500">
          {slides[currentIndex].description2}
        </p>
        <Link href={slides[currentIndex].link}>
          <Button className="item-mama opacity-0 bg-icon text-white text-sm lg:text-base rounded-md px-2 sm:px-4 pt-6 pb-7 flex items-center gap-4 mt-4 sm:mt-8 transition-all duration-500">
            Start Shopping <MdOutlineArrowForwardIos />
          </Button>
        </Link>
      </div>
    </>
  );
};

export default Left;
